import React from 'react';

import * as mapUtils from 'app/utils/mapUtils';

interface Props {
  map: mapboxgl.Map;
  style?: string;
}

const MapStyle: React.FC<Props> = ({
  map,
  style = mapUtils.getMapboxUri(mapUtils.DEFAULT_STYLE),
}) => {
  /*
   * Use a ref for the map to avoid triggering effects when the map reference changes
   *  The map reference changes often, but the underlying value is often the same,
   * so watching each separately avoids unnecessary updates.
   */
  const mapRef = React.useRef<mapboxgl.Map>(map);
  React.useEffect(() => {
    mapRef.current = map;
  }, [map]);

  const styleRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const updateStyle = async () => {
      mapRef.current.setStyle(mapUtils.getMapboxUri(style), {
        /*
         * Set diff to false because by default setStyle removes the layers we added
         */
        diff: false,
        localFontFamily: undefined,
        localIdeographFontFamily: undefined,
      });
      await mapUtils.loadIconImages(mapRef.current);
      styleRef.current = style;
    };

    /*
     * If the map style hasn't loaded set it, otherwise check if the style has changed
     */
    if (!mapRef.current.isStyleLoaded()) {
      updateStyle();
      return;
    }

    const currentStyle = mapRef.current.getStyle();
    const currentStyleId = currentStyle ? currentStyle.metadata?.['mapbox:origin'] : null;

    // Only update if the style is different from both the last applied style and the current map style
    if (style !== styleRef.current && style !== currentStyleId) {
      updateStyle();
    }
  }, [style]); // Only depend on style changes, not map object changes

  return null;
};

export default MapStyle;
